"use client";

import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { 
  // Dropdown, 
  FormBox, 
  // Input, 
  Label,
  MultiInputDropdown,
  // MultiInputOption,
  SelectedOption
} from "@int-traderjoes/ui-components-library";
import { useTheme } from "@/context";
import { useLanguageContext } from "@/context/LanguageContext";
import AuthService from "@/services/authService";
import { generateTemplate } from "@/utils/helper";
// import { ALLERGEN_DECLARATION_OPTIONS } from "@/constants/allergenDeclarationOptions"; // Kept for reference but not used

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Image from "next/image";
import deleteIcon from "@/assets/images/ideation-phase-images/DeleteIcon.svg";
import {
  containerStyles,
  headerStyles,
  headerTitleStyles,
  contentStyles,
  declarationSectionStyles,
  declarationInputStyles,
  fullWidthBoxStyles,

  declarationLabelStyles,
  declarationValueStyles
} from "./styles";

export interface AllergenDeclarationData {
  selectedAllergen: string;
  contains: string;
  mayContain: string;
  otherAllergenDeclarations: Array<{
    option: {
      id: string;
      label: string;
      template: Array<{
        type: "text" | "input";
        value?: string;
      }>;
    };
    inputs: (string | null)[];
  }>;
  textFields: Record<string, string>;
}

interface AllergenDeclarationProps {
  selectedAllergen: string;
  onDataChange?: (data: AllergenDeclarationData) => void;
  initialData?: Partial<AllergenDeclarationData>;
  errors?: string[];
  onFieldFocus?: (fieldName: string) => void;
  onFieldBlur?: (fieldName: string) => void;
  onUserEditingChange?: (editing: boolean) => void;
  declarationOptions?: any[]; // Options from API
  readOnly?: boolean; // Disable all form fields when page is locked
}

const AllergenDeclaration = forwardRef<any, AllergenDeclarationProps>(({ 
  selectedAllergen,
  onDataChange,
  initialData,
  errors = [],
  onFieldFocus,
  onFieldBlur,
  onUserEditingChange,
  declarationOptions = [], // Will be provided from parent component
  readOnly = false // Disable all form fields when page is locked
}, ref) => {
  const { darkMode } = useTheme();
  const { languageText } = useLanguageContext();
  
  // Theme class constant
  const themeClass = darkMode ? "dark" : "light";
  
  // Get labels from language context
  const allergenDeclarationLabels = languageText?.TABLE_AND_FORM_HEADER?.TABLE_HEADER?.ALLERGEN_DECLARATION;
  
  // Get permissions for allergen declaration fields
  const permissions = AuthService.fetchPermissions("AllergenDeclaration");
  
  const [isExpanded, setIsExpanded] = useState(true);
  // const [otherAllergenDeclarations, setOtherAllergenDeclarations] = useState<SelectedOption[]>([]);
  const [formData, setFormData] = useState<AllergenDeclarationData>({
    selectedAllergen,
    contains: initialData?.contains || "",
    mayContain: initialData?.mayContain || "",
    otherAllergenDeclarations: initialData?.otherAllergenDeclarations || [],
    textFields: initialData?.textFields || {},
    ...initialData
  });

  
  // State for multiple text fields
  const [textFields, setTextFields] = useState<Array<{id: string; type: string; value: string; value2?: string}>>([]);

  // Track if user has edited otherAllergenDeclarations to prevent rehydration
  const userEditedOtherDeclsRef = useRef(false);

  // Handle MultiInputDropdown changes
  const handleOtherAllergenDeclarationsChange = (selectedOptions: SelectedOption[]) => {
    // Block changes when readOnly
    if (readOnly) {
      return;
    }
    console.log("🔄 MultiInputDropdown onChange:", selectedOptions);
    
    // Signal that user is editing declarations to prevent overwrites
    if (onUserEditingChange) {
      onUserEditingChange(true);
      // Reset the flag after a short delay to allow the change to process
      setTimeout(() => onUserEditingChange(false), 100);
    }
    
    // Convert to template-based structure for formData (store name instead of id, same as Packaging MultiInputDropdown)
    const templateBasedDeclarations = selectedOptions.map((opt: any) => {
      return {
        option: {
          id: opt?.option?.name ?? opt?.option?.id ?? opt?.option?.value ?? "",
          label: opt?.option?.label ?? "",
          template: opt?.option?.template ?? []
        },
        inputs: opt?.inputs ?? []
      };
    });

    userEditedOtherDeclsRef.current = true;
    const newFormData = {
      ...formData,
      otherAllergenDeclarations: templateBasedDeclarations
    };
    setFormData(newFormData);
    
    // Notify parent of data change to clear errors
    if (onDataChange) {
      onDataChange(newFormData);
    }
  };

  // Use options from API (or fallback to static)
  const generatedOptions = useMemo(() => declarationOptions, [declarationOptions]);
  
  // Dropdown options for Other Allergen Declarations (id from name, same as Packaging MultiInputDropdown)
  const otherAllergenOptions = useMemo(() => {
    const dynamicOptions = generatedOptions.map(option => ({
      label: option.label,
      id: String(option?.name ?? option?.id ?? ''),
    }));
    return dynamicOptions;
  }, [generatedOptions]);

  // Track if form has been initialized to prevent overwriting user changes
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  useEffect(() => {
    console.log("🔄 [AllergenDeclaration] InitialData changed:", initialData);
    
    // Only reset formData on initial load or when initialData is completely new
    if (!isFormInitialized) {
      setFormData((prev) => {
        if (!initialData) {
          // Reset to default values when no initial data (variant change)
          const resetData = {
            selectedAllergen,
            contains: "",
            mayContain: "",
            otherAllergenDeclarations: [],
            textFields: {}
          };
          console.log("🔄 [AllergenDeclaration] Resetting to default values:", resetData);
          return resetData;
        }
        
        // Use initial data when available
        const newData = {
          ...prev,
          ...initialData
        };
        console.log("🔄 [AllergenDeclaration] Setting initial data:", newData);
        return newData;
      });
      setIsFormInitialized(true);
    }
  }, [initialData, selectedAllergen, isFormInitialized]);

  // Update form data when selectedAllergen changes (only if not initialized)
  useEffect(() => {
    if (isFormInitialized) {
      setFormData(prev => ({
        ...prev,
        selectedAllergen
      }));
    }
  }, [selectedAllergen, isFormInitialized]);

  // Keep contains/mayContain in sync with incoming initialData (computed from modal edits)
  useEffect(() => {
    if (!isFormInitialized) return;
    if (initialData && (
      initialData.contains !== formData.contains || 
      initialData.mayContain !== formData.mayContain
    )) {
      setFormData(prev => ({
        ...prev,
        contains: initialData.contains || "",
        mayContain: initialData.mayContain || "",
      }));
    }
  }, [initialData?.contains, initialData?.mayContain, formData.contains, formData.mayContain, isFormInitialized]);

  // Hydrate otherAllergenDeclarations from initialData once when it becomes available (post-GET)
  useEffect(() => {
    if (!isFormInitialized) return;
    if (userEditedOtherDeclsRef.current) return; // do not overwrite user-cleared/edited state
    const incoming = initialData?.otherAllergenDeclarations || [];
    const hasIncoming = Array.isArray(incoming) && incoming.length > 0;
    const hasCurrent = Array.isArray(formData.otherAllergenDeclarations) && formData.otherAllergenDeclarations.length > 0;

    // Only set if current is empty to avoid overwriting user edits
    if (hasIncoming && !hasCurrent) {
      setFormData(prev => ({
        ...prev,
        otherAllergenDeclarations: incoming as any
      }));
      // Mark as hydrated to avoid future rehydrations
      userEditedOtherDeclsRef.current = true;
    }
  }, [initialData?.otherAllergenDeclarations, isFormInitialized, formData.otherAllergenDeclarations]);

  // Notify parent of data changes (only for user-initiated changes)
  // useEffect(() => {
  //   if (onDataChange) {
  //     onDataChange(formData);
  //   }
  // }, [formData.otherAllergenDeclarations, onDataChange]); // Only notify for dropdown changes


  const handleDropdownChange = (field: keyof AllergenDeclarationData) => 
    (e: React.ChangeEvent<HTMLInputElement>, option: any) => {
      setFormData(prev => ({
        ...prev,
        [field]: option.value
      }));
    };

  const handleDirectChange = (field: keyof AllergenDeclarationData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Helper function to check if field has error
  const hasFieldError = (fieldName: string): boolean => {
    return errors.includes(fieldName);
  };

  // Helper function to add dot at the end if value exists
  const formatWithDot = (value: string): string => {
    if (!value || !value.trim()) return value;
    const trimmed = value.trim();
    return trimmed.endsWith('.') ? trimmed : `${trimmed}.`;
  };

  // Handle field focus
  const handleFieldFocus = (fieldName: string) => {
    if (onFieldFocus) {
      onFieldFocus(fieldName);
    }
  };

  // Handle field blur
  const handleFieldBlur = (fieldName: string) => {
    if (onFieldBlur) {
      onFieldBlur(fieldName);
    }
  };




  // Add text field based on dropdown selection
  const addTextFieldFromDropdown = (selectedOption: string) => {
    if (!selectedOption || readOnly) return;
    
    const fieldConfig = getFieldConfig(selectedOption);
    
    // Create field based on input type
    if (fieldConfig.inputType === 'double') {
      const newField = {
        id: `field_${Date.now()}`,
        type: selectedOption,
        value: "", // First input value
        value2: "" // Second input value
      };
      setTextFields(prev => [...prev, newField]);
    } else {
      const newField = {
        id: `field_${Date.now()}`,
        type: selectedOption,
        value: "" // Start with empty value - user will type their own text
      };
      setTextFields(prev => [...prev, newField]);
    }
  };

  // Handle text field value change
  const handleTextFieldChange = (fieldId: string, newValue: string) => {
    if (readOnly) return;
    setTextFields(prev => prev.map(field => 
      field.id === fieldId ? { ...field, value: newValue } : field
    ));
  };

  // Handle second text field value change (for generic replace)
  const handleTextField2Change = (fieldId: string, newValue: string) => {
    if (readOnly) return;
    setTextFields(prev => prev.map(field => 
      field.id === fieldId ? { ...field, value2: newValue } : field
    ));
  };

  // Handle text field deletion
  const handleDeleteTextField = (fieldId: string) => {
    if (readOnly) return;
    setTextFields(prev => prev.filter(field => field.id !== fieldId));
  };

  // Get field configuration based on field type (stored id may be name, same as Packaging)
  const getFieldConfig = (fieldType: string) => {
    const generatedOption = generatedOptions.find(
      option => option?.id === fieldType || option?.name === fieldType
    );
    if (generatedOption) {
      return {
        label: generatedOption.displayLabel,
        labelWidth: generatedOption.labelWidth,
        containerStyle: generatedOption.containerStyle,
        permissionKey: generatedOption.permissionKey,
        inputType: generatedOption.inputType,
        secondLabel: generatedOption.secondLabel
      };
    }
    
    // Fallback for any custom or unknown field types
    return { 
      label: 'Custom Text',
      labelWidth: '200px',
      containerStyle: 'normal',
      permissionKey: 'otherAllergenDeclarations',
      inputType: 'single',
      secondLabel: ''
    };
  };

  // Note: MultiInputDropdown now handles its own state and calls handleOtherAllergenDeclarationsChange
  // No need for useEffect to watch formData.otherAllergenDeclarations changes

  // Expose formData via ref to avoid rerender issues
  useImperativeHandle(ref, () => ({
    getCurrentData: () => formData,
    getFormData: () => formData
  }), [formData]);

  // Notify parent component when formData changes (commented out to prevent rerenders)
  useEffect(() => {
    // if (onDataChange) {
    //   console.log("🔄 AllergenDeclaration notifying parent of data change:", formData);
    //   onDataChange(formData);
    // }
  }, [formData]);

  return (
    <FormBox title={allergenDeclarationLabels?.DECLARATIONS || "Declarations"} darkModeClass={themeClass} collapsable={true} collapsed={false}>
      {isExpanded && (
        <Box sx={contentStyles}>
          <Box sx={declarationSectionStyles}>
            <Box sx={declarationInputStyles(darkMode)}>
              <Typography sx={declarationLabelStyles(darkMode)}>
                {allergenDeclarationLabels?.CONTAINS || "CONTAINS"}
              </Typography>
              <Typography sx={declarationValueStyles(darkMode)}>
                {formatWithDot(formData.contains)}
              </Typography>
            </Box>

            <Box sx={declarationInputStyles(darkMode)}>
              <Typography sx={declarationLabelStyles(darkMode)}>
                {allergenDeclarationLabels?.MAY_CONTAIN || "MAY CONTAIN"}
              </Typography>
              <Typography sx={declarationValueStyles(darkMode)}>
                {formatWithDot(formData.mayContain)}
              </Typography>
            </Box>
          </Box>

          <Box sx={fullWidthBoxStyles}>
            <Box mb={1}>
              <Label
                text={allergenDeclarationLabels?.OTHER_ALLERGEN_DECLARATIONS || "Other Allergen Declarations"}
                required={permissions?.otherAllergenDeclarations?.isMandatory || false}
                className={`${themeClass} ${hasFieldError('otherAllergenDeclarations') ? 'label-error-text' : ''}`}
              />
            </Box>
            <MultiInputDropdown 
              options={otherAllergenOptions} 
              onChange={handleOtherAllergenDeclarationsChange} 
              replaceKey={"{}"} 
              value={formData.otherAllergenDeclarations}
              className={`${themeClass} ${hasFieldError('otherAllergenDeclarations') ? 'field-error-color' : ''}`}
              disabled={readOnly}
            />
          </Box>
        </Box>
      )}
    </FormBox>
  );
});

AllergenDeclaration.displayName = 'AllergenDeclaration';

export default AllergenDeclaration;
